import type React from 'react';
import {useState} from 'react';
import {
    Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField,
    Typography,
} from '@mui/material';
import {errorText} from '../../hooks/notices';
import type {RiskOverrideInput} from '../../hooks/useRiskActions';
import type {RiskScore} from '../../types/categoryProfileTypes';

const LEVELS = [{value: '', label: 'Keep the calculated level'}, {value: 'high', label: 'High risk'},
    {value: 'medium', label: 'Medium risk'}, {value: 'low', label: 'Low risk'}];

type Props = {score: RiskScore; onSubmit: (input: RiskOverrideInput) => Promise<unknown>; onClose: () => void};

/** Set the category's risk level by hand and/or leave factors out of its score, with the reason the team sees. */
export const RiskOverrideDialog: React.FC<Props> = ({score, onSubmit, onClose}) => {
    const current = score.override;
    const [level, setLevel] = useState<string>(current?.level ?? '');
    const [excluded, setExcluded] = useState<string[]>(current?.excluded ?? []);
    const [reason, setReason] = useState(current?.reason ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ready = (level || excluded.length > 0) && reason.trim();

    const submit = () => {
        setBusy(true);
        setError(null);
        onSubmit({level: (level || null) as RiskOverrideInput['level'], excluded: excluded as RiskOverrideInput['excluded'],
            reason: reason.trim()})
            .then(onClose).catch(e => setError(errorText(e))).finally(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="cp-risk-override-title">
            <DialogTitle id="cp-risk-override-title">Change the risk</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Typography variant="body2" color="text.secondary">
                        {`Calculated: ${score.calculatedLevel ?? score.level}. Your input stays through data refreshes; when a refresh `
                            + 'changes the calculated level, the team is told.'}
                    </Typography>
                    <TextField select label="Level" size="small" value={level} onChange={e => setLevel(e.target.value)}>
                        {LEVELS.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                    </TextField>
                    <Stack>
                        <Typography variant="subtitle2">Leave out of this category&apos;s score</Typography>
                        {score.factors.map(f => (
                            <FormControlLabel key={f.key} label={f.label} control={
                                <Checkbox size="small" checked={excluded.includes(f.key)}
                                          onChange={(_, on) => setExcluded(on ? [...excluded, f.key] : excluded.filter(k => k !== f.key))}/>}/>
                        ))}
                    </Stack>
                    <TextField label="Reason" size="small" required multiline minRows={2} value={reason}
                               onChange={e => setReason(e.target.value)} slotProps={{htmlInput: {maxLength: 2000}}}
                               placeholder="e.g. Framework agreement with guaranteed supply"
                               helperText="Shown next to the level, with your name and the date"/>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={!ready || busy}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};
